/**
 * ChronologyBuildLane — "Build with Arc" for one chronology.
 *
 * An in-tab panel (not a drawer — results land right below it) holding a
 * chronology-scoped Arc conversation (one resumable conversation per
 * chronology — `/build/conversation` get-or-create). Turns go through the
 * standard chat pipeline with `chronology_build_target` in the body, which
 * force-activates the chronology_build toolkit and injects a per-turn
 * chronology state block server-side. Arc's `propose_build_plan` result
 * renders as a BuildPlanCard (via ChatMessage.TOOL_CARDS); Run goes through
 * the deterministic `/build/run` endpoint here — Arc never writes entries.
 *
 * Chat mechanics mirror ChronologyRefineDrawer (useChatEvents poll, event
 * fold, sequence cursor, resume-if-streaming).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';
import useChatEvents from '../hooks/useChatEvents';
import useHelpTarget from '../hooks/useHelpTarget';
import ChatMessage from './ChatMessage';
import { ChronologyBuildContext } from './chronologyBuildContext';
import { AGENT_NAME_SHORT } from '../brand';

const S = {
  panel: { marginTop: 12, marginBottom: 12, border: '1px solid #d9cdf5', borderRadius: 10, background: '#fff', overflow: 'hidden' },
  head: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: '#f6f4fc', borderBottom: '1px solid #eee6fb' },
  title: { fontWeight: 700, fontSize: 14 },
  startOver: { padding: '4px 10px', borderRadius: 6, border: '1px solid #c7ccd6', background: '#fff', cursor: 'pointer', fontSize: 12, color: '#556' },
  close: { background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: '#667', padding: 4 },
  body: { maxHeight: 420, overflowY: 'auto', padding: '10px 14px' },
  progressLine: { fontSize: 12, color: '#8a7fae', padding: '2px 0' },
  streamText: { fontSize: 13, whiteSpace: 'pre-wrap', padding: '6px 0' },
  err: { color: '#c0392b', fontSize: 13, padding: '6px 0' },
  composer: { display: 'flex', gap: 8, padding: '10px 14px', borderTop: '1px solid #eee6fb', background: '#fafbfc' },
  input: { flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid #c7ccd6', fontSize: 13, resize: 'none', fontFamily: 'inherit' },
  send: { padding: '8px 14px', borderRadius: 8, border: '1px solid #5b3fa8', background: '#5b3fa8', color: '#fff', cursor: 'pointer', fontSize: 13 },
  stop: { padding: '8px 14px', borderRadius: 8, border: '1px solid #c0392b', background: '#fff', color: '#c0392b', cursor: 'pointer', fontSize: 13 },
  emptyHint: { color: '#778', fontSize: 13, padding: '14px 4px', lineHeight: 1.5 },
  exampleRow: { display: 'flex', gap: 6, flexWrap: 'wrap', paddingTop: 8 },
  example: { padding: '4px 10px', borderRadius: 14, border: '1px solid #d9cdf5', background: '#faf8ff', cursor: 'pointer', fontSize: 12, color: '#5b3fa8' },
};

const EXAMPLES = [
  'Build a timeline of the central incident and everything that led up to it',
  'A chronology of every interview and statement, in order',
  'What events am I missing from this chronology?',
];

/* Same minimal event fold as the refine drawer (text, tool trace, progress,
 * done/error). */
function processEvents(events) {
  let text = '';
  const toolCalls = [];
  const toolResults = [];
  const progress = [];
  let done = null;
  let error = null;
  for (const evt of events) {
    const t = evt.event_type || evt.type;
    const d = evt.data ?? evt;
    switch (t) {
      case 'streaming_text':
        text += d.text ?? d.chunk ?? '';
        break;
      case 'tool_call':
        toolCalls.push({ tool_name: d.tool_name ?? d.name ?? d.tool, args: d.args ?? {}, sequence: evt.sequence });
        break;
      case 'tool_result':
        toolResults.push({
          tool_name: d.tool_name ?? d.name ?? d.tool,
          result: d.result ?? d.output,
          duration_ms: d.duration_ms,
          sequence: evt.sequence,
        });
        break;
      case 'progress': {
        const msg = d.message ?? d.text ?? d.finding ?? d.step_name;
        if (msg) {
          const last = progress[progress.length - 1];
          if (!last || last !== msg) progress.push(msg);
        }
        break;
      }
      case 'done':
        done = d;
        break;
      case 'error':
        error = d.error ?? d.message ?? 'Unknown error';
        break;
      default:
        break;
    }
  }
  const trace = toolCalls.map((tc) => {
    const tr = toolResults.find((r) => r.tool_name === tc.tool_name && r.sequence > tc.sequence);
    return { ...tc, result: tr?.result, duration_ms: tr?.duration_ms };
  });
  return { text, trace, progress, done, error };
}

export default function ChronologyBuildLane({
  caseId, chronologyId, onClose, onRan,
}) {
  const { token } = useAuth();

  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingConv, setLoadingConv] = useState(true);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState('');
  const [streamProgress, setStreamProgress] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [reports, setReports] = useState({});
  const [addedEvents, setAddedEvents] = useState({});
  const [mergedGroups, setMergedGroups] = useState({});
  const [freshNonce, setFreshNonce] = useState(0);

  const allEventsRef = useRef([]);
  const seqRef = useRef(0);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  const finishTurn = useCallback((state) => {
    const d = state.done || {};
    const content = d.answer ?? state.text;
    if (content || (d.tool_trace || state.trace).length) {
      setMessages((ms) => [...ms, {
        role: 'assistant',
        content: content || '',
        tool_trace: d.tool_trace ?? state.trace,
        citations: d.citations,
        partial: d.partial || d.status === 'cancelled' || d.status === 'budget_exceeded',
      }]);
    }
    setStreaming(false);
    setStreamText('');
    setStreamProgress([]);
    allEventsRef.current = [];
  }, []);

  const onEvents = useCallback((events) => {
    // Advance the sequence cursor (the replayed-old-message lesson from the
    // refine drawer — never restart the poll at seq 0 on a follow-up turn).
    for (const e of events) {
      const seq = e.sequence ?? 0;
      if (seq > seqRef.current) seqRef.current = seq;
    }
    allEventsRef.current = [...allEventsRef.current, ...events];
    const state = processEvents(allEventsRef.current);
    setStreamText(state.text);
    setStreamProgress(state.progress.slice(-4));
    if (state.error) {
      setError(state.error);
      setStreaming(false);
      allEventsRef.current = [];
      return;
    }
    if (state.done) finishTurn(state);
  }, [finishTurn]);

  const onStatusChange = useCallback((status) => {
    if (status === 'completed' || status === 'interrupted' || status === 'failed') {
      if (allEventsRef.current.length) finishTurn(processEvents(allEventsRef.current));
      else setStreaming(false);
    }
  }, [finishTurn]);

  const chatEvents = useChatEvents(caseId, conversationId, {
    getToken: () => token, onEvents, onStatusChange, intervalMs: 500,
  });
  const chatEventsRef = useRef(chatEvents);
  useEffect(() => { chatEventsRef.current = chatEvents; }, [chatEvents]);

  // Open the chronology's build conversation. Resumable across opens
  // (fresh only on "Start over") — a build is an ongoing project, unlike the
  // per-entry refine drawer's fresh-by-default. Deps are the scoped
  // chronology + the fresh nonce only (self-cancel load-hang lesson).
  useEffect(() => {
    if (!chronologyId) return undefined;
    let cancelled = false;
    setLoadingConv(true);
    setConversationId(null);
    setMessages([]);
    setError('');
    setStreaming(false);
    allEventsRef.current = [];
    (async () => {
      try {
        const conv = await api.post(
          `/cases/${caseId}/chronologies/${chronologyId}/build/conversation`,
          { fresh: freshNonce > 0 },
        );
        if (cancelled) return;
        setConversationId(conv.conversation_id);
        seqRef.current = 0;
        if (!conv.created) {
          // Existing conversation — load its history so the lane resumes
          // where the build left off.
          const detail = await api.get(
            `/exploration/case/${caseId}/conversations/${conv.conversation_id}`,
          );
          if (cancelled) return;
          setMessages((detail.messages || []).map((m) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            tool_trace: m.tool_trace,
            citations: m.citations,
          })));
          seqRef.current = detail.last_event_seq || 0;
          if (conv.resumed_streaming) {
            setStreaming(true);
            chatEventsRef.current.start(seqRef.current, conv.conversation_id);
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to open the build conversation');
      } finally {
        if (!cancelled) setLoadingConv(false);
      }
    })();
    return () => { cancelled = true; chatEventsRef.current.stop(); };
  }, [caseId, chronologyId, freshNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages, streamText]);

  useEffect(() => {
    if (!streaming && !loadingConv) inputRef.current?.focus();
  }, [streaming, loadingConv]);

  const send = async (e, presetText) => {
    e?.preventDefault();
    const text = (presetText ?? input).trim();
    if (!text || streaming || !conversationId) return;
    setMessages((ms) => [...ms, { role: 'user', content: text }]);
    setInput('');
    setError('');
    setStreaming(true);
    setStreamText('');
    setStreamProgress([]);
    allEventsRef.current = [];
    try {
      await api.post(`/exploration/case/${caseId}/chat/start`, {
        message: text,
        conversation_id: conversationId,
        chronology_build_target: { chronology_id: chronologyId },
      });
      chatEventsRef.current.start(seqRef.current, conversationId);
    } catch (err) {
      setError(err.message || 'Failed to send');
      setStreaming(false);
    }
  };

  const stop = async () => {
    try {
      await api.post(`/exploration/case/${caseId}/chat/cancel/${conversationId}`);
    } catch { /* the poll loop surfaces the final state */ }
  };

  const run = useCallback(async (plan, planKey) => {
    setBusy(true);
    try {
      const report = await api.post(
        `/cases/${caseId}/chronologies/${chronologyId}/build/run`,
        { ...plan, conversation_id: conversationId },
      );
      setReports((r) => ({ ...r, [planKey]: report }));
      onRan?.(report);
      return report;
    } finally {
      setBusy(false);
    }
  }, [caseId, chronologyId, conversationId, onRan]);

  // Source-drafted events (EventDraftCard "Add") → deterministic endpoint.
  const addEvents = useCallback(async (events, key) => {
    setBusy(true);
    try {
      const report = await api.post(
        `/cases/${caseId}/chronologies/${chronologyId}/entries/from-sources`,
        { events, conversation_id: conversationId },
      );
      setAddedEvents((r) => ({ ...r, [key]: report }));
      onRan?.(report);
      return report;
    } finally {
      setBusy(false);
    }
  }, [caseId, chronologyId, conversationId, onRan]);

  // Merge groups (MergeProposalCard "Apply") → deterministic merge endpoint;
  // the server re-validates the group (entries live, same chronology).
  const mergeGroup = useCallback(async (payload, key) => {
    setBusy(true);
    try {
      await api.post(
        `/cases/${caseId}/chronologies/${chronologyId}/entries/merge`,
        payload,
      );
      setMergedGroups((r) => ({ ...r, [key]: new Date().toISOString() }));
      onRan?.({ merged: 1 });
    } finally {
      setBusy(false);
    }
  }, [caseId, chronologyId, onRan]);

  // Contains-group default: keep the summary as an EPISODE (nest — display
  // hierarchy, destroys nothing) rather than absorbing it.
  const nestGroup = useCallback(async (parentId, childIds, key) => {
    setBusy(true);
    try {
      await api.post(
        `/cases/${caseId}/chronologies/${chronologyId}/entries/nest`,
        { parent_entry_id: parentId, child_entry_ids: childIds },
      );
      setMergedGroups((r) => ({ ...r, [key]: new Date().toISOString() }));
      onRan?.({ nested: childIds.length });
    } finally {
      setBusy(false);
    }
  }, [caseId, chronologyId, onRan]);

  const buildCtx = useMemo(
    () => ({ run, reports, addEvents, addedEvents, mergeGroup, nestGroup, mergedGroups, busy, chronologyId }),
    [run, reports, addEvents, addedEvents, mergeGroup, nestGroup, mergedGroups, busy, chronologyId],
  );

  const helpRef = useHelpTarget({
    slug: 'building-a-chronology-with-arc',
    label: 'Build with Arc',
    question: 'How does Build with Arc work, and what does running a build plan do?',
  });

  return (
    <ChronologyBuildContext.Provider value={buildCtx}>
      <div style={S.panel} ref={helpRef}>
        <div style={S.head}>
          <span style={S.title}>✨ Build with {AGENT_NAME_SHORT}</span>
          <span style={{ fontSize: 12, color: '#8a7fae' }}>
            describe the timeline you need — {AGENT_NAME_SHORT} scopes it, you approve the plan
          </span>
          <span style={{ flex: 1 }} />
          {messages.length > 0 && !streaming && (
            <button
              style={S.startOver}
              onClick={() => setFreshNonce((n) => n + 1)}
              title="Start a new build conversation (the old one moves to the case's conversation history)"
            >⟳ Start over</button>
          )}
          {onClose && <button style={S.close} onClick={onClose} title="Collapse">✕</button>}
        </div>

        <div style={S.body}>
          {loadingConv && <div style={S.emptyHint}>Opening the conversation…</div>}
          {!loadingConv && messages.length === 0 && !streaming && (
            <div style={S.emptyHint}>
              Tell {AGENT_NAME_SHORT} what timeline you need — a topic, the people
              involved, a date window, or just “everything important.” It explores
              the case’s extracted events and comes back with a plan card showing
              exactly what it would add. Nothing is written until you click Run,
              and everything lands as suggestions for your review.
              <div style={S.exampleRow}>
                {EXAMPLES.map((ex) => (
                  <button key={ex} type="button" style={S.example} onClick={(e) => send(e, ex)}>
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <ChatMessage key={m.id || `m-${i}`} msg={m} filenameMap={{}} token={token} />
          ))}
          {streaming && (
            <div>
              {streamProgress.map((p, i) => <div key={i} style={S.progressLine}>{p}</div>)}
              {streamText
                ? <div style={S.streamText}>{streamText}</div>
                : <div style={S.progressLine}>{AGENT_NAME_SHORT} is working…</div>}
            </div>
          )}
          {error && <div style={S.err}>{error}</div>}
          <div ref={endRef} />
        </div>

        <form style={S.composer} onSubmit={send}>
          <textarea
            ref={inputRef}
            style={S.input}
            rows={2}
            placeholder={`Describe the timeline you need…`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
            }}
            disabled={loadingConv}
          />
          {streaming
            ? <button type="button" style={S.stop} onClick={stop}>Stop</button>
            : <button type="submit" style={S.send} disabled={loadingConv || !input.trim()}>Send</button>}
        </form>
      </div>
    </ChronologyBuildContext.Provider>
  );
}
